import React, { Suspense, useMemo } from "react";
import { Canvas, useLoader } from "@react-three/fiber";
import { OrbitControls, Environment, Html } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import * as THREE from "three";

const MODEL_PATHS = {
  male: "/models/hra/male-united.glb",
  female: "/models/hra/female-united.glb",
};

const ORGAN_COLORS = {
  low: "#22c55e",
  moderate: "#f59e0b",
  high: "#ef4444",
  unknown: "#8b5cf6",
};

const ORGAN_ALIASES = {
  brain: ["brain", "cerebrum", "cerebellum", "neural"],
  heart: ["heart", "cardiac"],
  lung: ["lung", "lungs", "pulmonary"],
  lungs: ["lung", "lungs", "pulmonary"],

  liver: ["liver", "hepatic"],
  kidney: ["kidney", "renal"],
  kidneys: ["kidney", "renal"],
  stomach: ["stomach", "gastric"],
  intestine: [
    "intestine",
    "intestinal",
    "colon",
    "ileum",
    "jejunum",
    "duodenum",
    "rectum",
  ],
  small_intestine: [
    "small intestine",
    "small_intestine",
    "ileum",
    "jejunum",
    "duodenum",
  ],
  large_intestine: ["large intestine", "large_intestine", "colon", "rectum"],
  pancreas: ["pancreas", "pancreatic"],
  spleen: ["spleen", "splenic"],
  gallbladder: ["gallbladder", "gall bladder"],

  thyroid: ["thyroid"],
  adrenal: ["adrenal"],
  pituitary: ["pituitary"],
  thymus: ["thymus"],

  skin: ["skin", "integument"],
  bone_marrow: ["bone marrow", "bone_marrow", "marrow"],
  bone: [
    "bone",
    "skeleton",
    "skull",
    "rib",
    "vertebra",
    "femur",
    "humerus",
    "pelvis",
    "tibia",
  ],
  skeleton: [
    "bone",
    "skeleton",
    "skull",
    "rib",
    "vertebra",
    "femur",
    "humerus",
    "pelvis",
    "tibia",
  ],

  muscle: ["muscle", "muscular"],
  blood: ["blood", "vascular", "artery", "vein", "aorta"],
  blood_vessel: ["blood", "vascular", "artery", "vein", "aorta"],
  blood_vessels: ["blood", "vascular", "artery", "vein", "aorta"],
  lymph: ["lymph", "lymphatic"],

  ovaries: ["ovary", "ovaries"],
  ovary: ["ovary", "ovaries"],
  uterus: ["uterus", "uterine"],
  testes: ["testis", "testes", "testicle"],
  testis: ["testis", "testes", "testicle"],
  penis: ["penis", "penile"],
  prostate: ["prostate"],

  bladder: ["bladder", "urinary bladder"],
  ureter: ["ureter"],
  urethra: ["urethra"],

  eye: [
    "eye",
    "ocular",
    "retina",
    "eyeball",
    "eyelid",
    "conjunctiva",
    "cornea",
    "iris",
    "pupil",
  ],
  ear: ["ear", "cochlea"],
  mouth: ["mouth", "oral", "tongue"],
  esophagus: ["esophagus", "oesophagus"],
  trachea: ["trachea"],
};

const FACE_MASK_COLORS = {
  oval: "#22d3ee",
  jaw: "#34d399",
  eyes: "#a78bfa",
  brow: "#facc15",
  nose: "#38bdf8",
  mouth: "#f472b6",
  points: "#e0f2fe",
};

const FACE_MASK_CONFIG = {
  male: {
    position: [0, 2.20, 0.31],
    scale: 0.47,
  },
  female: {
    position: [0, 2.20, 0.31],
    scale: 0.45,
  },
};

export default function HraHumanBodyViewer({
  gender = "male",
  organs = {},
  transparent = true,
  faceProfile = null,
  showFaceMask = false,
}) {
  const normalizedGender = gender === "female" ? "female" : "male";
  const modelPath = MODEL_PATHS[normalizedGender];

  return (
    <div className="hra-body-viewer">
      {showFaceMask && faceProfile && (
        <div className="hra-viewer-chip">Avatar face geometry attached</div>
      )}

      <Canvas
        camera={{ position: [0, 1.45, 3.25], fov: 28 }}
        gl={{ antialias: true, alpha: true }}
      >
        <ambientLight intensity={0.9} />
        <directionalLight position={[4, 5, 5]} intensity={1.5} />
        <directionalLight position={[-4, 2, 3]} intensity={0.55} />

        <Suspense fallback={<Loading />}>
          <group>
            <HraModel
              modelPath={modelPath}
              organs={organs}
              transparent={transparent}
            />

            {showFaceMask && faceProfile && (
              <TransparentFaceMask
                faceProfile={faceProfile}
                gender={normalizedGender}
              />
            )}
          </group>

          <Environment preset="studio" />
        </Suspense>

        <OrbitControls
          enablePan={false}
          minDistance={1.8}
          maxDistance={6}
          target={[0, 1.05, 0]}
        />
      </Canvas>
    </div>
  );
}

function HraModel({ modelPath, organs, transparent }) {
  const gltf = useLoader(GLTFLoader, modelPath);

  const scene = useMemo(() => {
    const cloned = gltf.scene.clone(true);

    cloned.traverse((child) => {
      if (!child.isMesh) return;

      const meshName = `${child.name || ""}`.toLowerCase();
      const organMatch = findOrganMatch(meshName, organs);

      child.material =
        child.material?.clone?.() || new THREE.MeshStandardMaterial();

        if (organMatch) {
          const risk = organs[organMatch]?.risk || "low";
          const score = clamp01(Number(organs[organMatch]?.score || 0));
          const color = ORGAN_COLORS[risk] || ORGAN_COLORS.unknown;
          const isOuterSkin = isLikelyOuterBody(meshName) || meshName.includes("skin");

          child.material.color = new THREE.Color(color);
          child.material.emissive = new THREE.Color(color);

          if (isOuterSkin) {
            // Skin/body shell should remain transparent so internal anatomy stays visible.
            child.material.emissiveIntensity = Math.min(0.15 + score * 0.35, 0.55);
            child.material.transparent = true;
            child.material.opacity = Math.min(0.08 + score * 0.16, 0.22);
            child.material.depthWrite = false;
            child.material.depthTest = true;
            child.renderOrder = 2;
          } else {
            // Real internal impacted organs can be more visible.
            child.material.emissiveIntensity = Math.min(0.35 + score, 1.25);
            child.material.transparent = true;
            child.material.opacity = Math.min(0.45 + score * 0.45, 0.92);
            child.material.depthWrite = false;
            child.material.depthTest = true;
            child.renderOrder = 10;
          }

          child.material.roughness = 0.35;
        }
      
        else if (transparent) {
        child.material.color = new THREE.Color("#7dd3fc");
        child.material.transparent = true;
        // Keep the outer body very faint. Internal anatomy should remain visible.
        child.material.opacity = isLikelyOuterBody(meshName) ? 0.035 : 0.16;
        child.material.depthWrite = false;
        child.material.depthTest = true;
        child.material.roughness = 0.2;
        child.material.metalness = 0.05;
        child.renderOrder = isLikelyOuterBody(meshName) ? 1 : 3;
      }
    });

    centerAndScaleModel(cloned);

    return cloned;
  }, [gltf, organs, transparent]);

  return <primitive object={scene} />;
}

function TransparentFaceMask({ faceProfile, gender }) {
  const maskData = useMemo(() => buildFaceMaskData(faceProfile), [faceProfile]);

  if (!maskData) return null;

  const config = FACE_MASK_CONFIG[gender === "female" ? "female" : "male"];

  return (
    <group
      position={config.position}
      scale={[config.scale, config.scale, config.scale]}
      renderOrder={80}
    >
      {/* <FaceOval width={maskData.width} height={maskData.height} /> */}

      <LandmarkLine
        points={maskData.jaw}
        color={FACE_MASK_COLORS.jaw}
        opacity={0.55}
        close={false}
      />

      <LandmarkLine
        points={maskData.leftBrow}
        color={FACE_MASK_COLORS.brow}
        opacity={0.72}
        close={false}
      />

      <LandmarkLine
        points={maskData.rightBrow}
        color={FACE_MASK_COLORS.brow}
        opacity={0.72}
        close={false}
      />

      <LandmarkLine
        points={maskData.leftEye}
        color={FACE_MASK_COLORS.eyes}
        opacity={0.95}
        close
      />

      <LandmarkLine
        points={maskData.rightEye}
        color={FACE_MASK_COLORS.eyes}
        opacity={0.95}
        close
      />

      <LandmarkLine
        points={maskData.nose}
        color={FACE_MASK_COLORS.nose}
        opacity={0.82}
        close={false}
      />

      <LandmarkLine
        points={maskData.mouth}
        color={FACE_MASK_COLORS.mouth}
        opacity={0.9}
        close
      />

      <LandmarkPoints points={maskData.featurePoints} />

      {/* <mesh position={[0, -maskData.height * 0.67, 0.01]} renderOrder={90}>
        <ringGeometry args={[0.035, 0.041, 32]} />
        <meshBasicMaterial
          color="#22d3ee"
          transparent
          opacity={0.75}
          depthTest={false}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh> */}
    </group>
  );
}

function FaceOval({ width, height }) {
  const ringPoints = useMemo(() => {
    const points = [];
    const segments = 128;
    const rx = width * 0.6;
    const ry = height * 0.66;

    for (let i = 0; i <= segments; i += 1) {
      const t = (i / segments) * Math.PI * 2;
      points.push({
        x: Math.cos(t) * rx,
        y: Math.sin(t) * ry,
        z: -0.002,
      });
    }

    return points;
  }, [width, height]);

  return (
    <>
      <LandmarkLine
        points={ringPoints}
        color={FACE_MASK_COLORS.oval}
        opacity={0.72}
        close={false}
      />

      <mesh position={[0, 0, -0.01]} renderOrder={70}>
        <circleGeometry args={[Math.max(width, height) * 0.55, 96]} />
        <meshBasicMaterial
          color={FACE_MASK_COLORS.oval}
          transparent
          opacity={0.035}
          depthWrite={false}
          depthTest={false}
          side={THREE.DoubleSide}
        />
      </mesh>
    </>
  );
}

function LandmarkLine({
  points = [],
  color = "#22d3ee",
  opacity = 1,
  close = false,
}) {
  const geometry = useMemo(() => {
    if (!points || points.length < 2) return null;

    const vectors = points.map((point) =>
      point instanceof THREE.Vector3
        ? point.clone()
        : new THREE.Vector3(
            Number(point.x || 0),
            Number(point.y || 0),
            Number(point.z || 0)
          )
    );

    if (close && vectors.length > 1) {
      vectors.push(vectors[0].clone());
    }

    return new THREE.BufferGeometry().setFromPoints(vectors);
  }, [points, close]);

  if (!geometry) return null;

  return (
    <line geometry={geometry} renderOrder={85}>
      <lineBasicMaterial
        color={color}
        transparent
        opacity={opacity}
        depthTest={false}
        depthWrite={false}
      />
    </line>
  );
}

function LandmarkPoints({ points = [] }) {
  if (!points.length) return null;

  return (
    <group renderOrder={95}>
      {points.map((point, index) => (
        <mesh
          key={`${point.x}-${point.y}-${index}`}
          position={[point.x, point.y, 0.018]}
        >
          <sphereGeometry args={[0.0065, 10, 10]} />
          <meshBasicMaterial
            color={FACE_MASK_COLORS.points}
            transparent
            opacity={0.86}
            depthTest={false}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
}

function buildFaceMaskData(faceProfile) {
  const eyeProfile = faceProfile?.eye_profile;
  const mapping = eyeProfile?.avatar_mapping;
  const faceBox = eyeProfile?.face_box;

  if (!mapping || !faceBox?.width || !faceBox?.height) return null;

  const width = 0.42;
  const height = 0.54;

  const project = (point) => {
    const nx =
      (Number(point.x || 0) - Number(faceBox.x || 0)) /
      Number(faceBox.width || 1);

    const ny =
      (Number(point.y || 0) - Number(faceBox.y || 0)) /
      Number(faceBox.height || 1);

    const centeredX = nx - 0.5;
    const centeredY = 0.5 - ny;

    return {
      x: centeredX * width,
      y: centeredY * height,
      z: 0,
    };
  };

  const jaw = (mapping.face_shape_points || []).map(project);
  const leftEye = (mapping.left_eye_points || []).map(project);
  const rightEye = (mapping.right_eye_points || []).map(project);
  const nose = (mapping.nose_points || []).map(project);
  const mouth = (mapping.mouth_points || []).map(project);
  const leftBrow = (mapping.left_brow_points || []).map(project);
  const rightBrow = (mapping.right_brow_points || []).map(project);

  const featurePoints = [
    ...leftEye,
    ...rightEye,
    ...nose,
    ...mouth,
    ...leftBrow,
    ...rightBrow,
  ];

  return {
    width,
    height,
    jaw,
    leftEye,
    rightEye,
    nose,
    mouth,
    leftBrow,
    rightBrow,
    featurePoints,
  };
}

function centerAndScaleModel(model) {
  const box = new THREE.Box3().setFromObject(model);
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();

  box.getSize(size);
  box.getCenter(center);

  model.position.sub(center);

  const maxAxis = Math.max(size.x, size.y, size.z);
  const scale = 2.65 / maxAxis;

  model.scale.setScalar(scale);

  model.position.y += 1.08;
  model.rotation.y = Math.PI * 0.03;
}

function findOrganMatch(meshName, organs) {
  const keys = Object.keys(organs || {});

  return keys.find((organ) => {
    const key = String(organ || "").toLowerCase();
    const aliases = ORGAN_ALIASES[key] || [key];

    return aliases.some((alias) =>
      meshName.includes(String(alias || "").toLowerCase())
    );
  });
}

function isLikelyOuterBody(meshName) {
  return (
    meshName.includes("body") ||
    meshName.includes("skin") ||
    meshName.includes("surface") ||
    meshName.includes("female") ||
    meshName.includes("male")
  );
}

function clamp01(value) {
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function Loading() {
  return (
    <Html center>
      <div className="hra-loading">Loading HRA body model...</div>
    </Html>
  );
}